/**
 * The consultant's daily limits as a member reads them (platform-core twin/copilot_limits.go):
 *
 *   GET /v1/twin/copilot/limits   the person's limit and their own count, the org's limit and its count, each since
 *                                 the start of its window, and whether the org has limits of its own. Nobody else's.
 *
 * Mounted with the consultant's other routes, so a platform without the consultant answers 404.
 */
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { statusOf } from "./error";
import { useBriefLive } from "./brief";

export interface CopilotMemberLimits {
  user_daily: number;
  org_daily: number;
  own_count: number;
  own_since: string;
  org_count: number;
  org_since: string;
  org_set: boolean;
  now: string;
}

export const COPILOT_LIMITS_KEY = ["copilot-limits"] as const;

export const copilotLimitsApi = {
  get: () => api.get<CopilotMemberLimits>("/v1/twin/copilot/limits"),
};

/** Whether a failed read means the platform has no consultant (the route is not served), which no retry changes. */
export function isConsultantOff(err: unknown): boolean {
  return statusOf(err) === 404;
}

export function useCopilotLimitsQuery() {
  const live = useBriefLive();
  return useQuery({
    queryKey: COPILOT_LIMITS_KEY,
    queryFn: copilotLimitsApi.get,
    enabled: live,
    staleTime: 60_000,
    retry: (failures, err) => !isConsultantOff(err) && failures < 2,
  });
}
